import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Récupérer les paramètres de l'URL (Supabase envoie le token dans le hash)
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    
    const token = params.get('access_token');
    const type = params.get('type');
    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');
    
    if (errorCode || errorDescription) {
      setError(errorDescription || 'Le lien de réinitialisation est invalide ou a expiré.');
      return;
    }
    
    if (type === 'recovery' && token) {
      setAccessToken(token);
    } else if (!token) {
      setError('Lien de réinitialisation invalide. Veuillez demander un nouveau lien.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken,
          new_password: password,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Mot de passe mis à jour avec succès !');
      } else {
        toast.error(data.detail || 'Une erreur est survenue');
        if (data.detail && data.detail.includes('expiré')) {
          setError(data.detail);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Afficher l'erreur si le lien est invalide
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-2xl text-center" data-testid="reset-error-card">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien expiré</h1>
          <p className="text-gray-600 mb-6">{error}</p>

          <Link to="/forgot-password">
            <Button 
              className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
              data-testid="request-new-link-btn"
            >
              Demander un nouveau lien
            </Button>
          </Link>
          
          <Link to="/login" className="block mt-4">
            <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
              Retour à la connexion
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Afficher le succès
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-2xl text-center" data-testid="reset-success-card">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Mot de passe mis à jour !</h1>
          <p className="text-gray-600 mb-6">
            Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
          </p>

          <Button 
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]"
            data-testid="go-to-login-btn"
          >
            Se connecter
          </Button>
        </Card>
      </div>
    );
  }

  // Formulaire de réinitialisation
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 shadow-2xl" data-testid="reset-password-card">
        <div className="flex items-center justify-center mb-8">
          <Logo />
        </div>
        
        <div className="flex items-center justify-center mb-6">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
            <Lock className="w-7 h-7 text-[#2E59D7]" />
          </div>
        </div>
        
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">Nouveau mot de passe</h1>
        <p className="text-center text-gray-600 mb-8">
          Créez un nouveau mot de passe pour votre compte.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="password">Nouveau mot de passe</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum 6 caractères</p>
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <div className="relative mt-1">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB] hover:from-[#0061A0] hover:to-[#2E59D7]" 
            disabled={loading}
            data-testid="reset-password-submit-btn"
          >
            {loading ? 'Mise à jour...' : 'Réinitialiser le mot de passe'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
